// ── THE WITHHELD GOVERNED METRIC ────────────────────────────────────────────
//
// A producer that REFUSES to compute a figure (an incomplete denominator, an
// unpriced cost basis) is doing the right thing. What was wrong was what
// happened next: the metric was dropped from the emitted rows entirely, so the
// reader never learned that the number deciding their question exists, is
// named, and is blocked on one thing.
//
// Standing Law 15 direction: badge and name, never hide. A metric the producer
// computed AS WITHHELD keeps its row — its governed label, an honest
// no-digits cell, and the producer's own reason — instead of vanishing. This
// is the opposite of a render suppression: it ADDS an honest named row and
// removes nothing. It never un-withholds a value, never invents one, and never
// lets an unknown count as zero in a total or a denominator.
//
// WHY THIS LIVES IN packages/lib — AND WHY IT IS THE ONLY COPY. The phrase a
// withheld cell carries is asked on both sides of the wire: a producer emits
// it and the workspace renderer recognises it (exact, whole-cell) to paint the
// honest gap cell. Two copies of one closed phrase drift, and a drifted phrase
// renders as a sentence pretending to be a number. AGENTS.md Law 5 —
// "contracts are single-sourced… defined once and imported, never retyped" —
// so `apps/web/.../workspace-withheld-figure-resolution.ts` now RE-EXPORTS
// this declaration rather than restating it, and a drift test pins the two
// names to the same frozen array.
//
// TWO CAUSES, NEVER ONE SENTENCE FOR BOTH. A figure can be absent because its
// evidence is not approved yet (`source_pending`) or because the computation
// itself has no answer — a ratio whose denominator is not positive even though
// every input is fully governed (`not_computable`). Labelling the second as
// source-pending sends the reader to connect a source that is already
// connected, which is the same fabricated-cause defect the gap copy layer
// exists to prevent. The cause is the PRODUCER's, declared with the row.
//
// This file carries NO company data, NO arithmetic, and no fence: it is the
// vocabulary a withheld row speaks.

/**
 * Why no governed figure exists for a metric the producer named anyway.
 *
 * `source_pending`    — the evidence this figure needs is not approved/connected.
 *                       Operator-actionable: connect the source.
 * `not_computable`    — the evidence IS governed and the computation still has
 *                       no answer (a non-positive denominator, an incomplete
 *                       basis). Nothing to connect; the condition is arithmetic.
 */
export const WITHHELD_GOVERNED_FIGURE_CAUSES = [
  'source_pending',
  'not_computable',
] as const;

export type WithheldGovernedFigureCause =
  (typeof WITHHELD_GOVERNED_FIGURE_CAUSES)[number];

/**
 * The exact cell string for each cause. A noun phrase where a figure would
 * stand, naming what is missing, carrying NO digits — so the anti-fabrication
 * fence has nothing to reject and nothing to admit.
 */
export const WITHHELD_GOVERNED_FIGURE_CELL_BY_CAUSE: Readonly<
  Record<WithheldGovernedFigureCause, string>
> = Object.freeze({
  source_pending: 'a figure pending an approved source',
  not_computable: 'a figure that cannot be computed',
});

/**
 * The producer's CLOSED set of withheld-figure phrases — every exact string a
 * governed emitter may place in a numeric cell that has no admitted binding.
 *
 * Three phrasings are live in the estate: the prose omission lane's, the
 * source-pending table cell, and the non-computable table cell. The renderer
 * matches on the whole trimmed cell — a cell that merely MENTIONS a phrase
 * inside a longer sentence is prose, not a withheld cell.
 *
 * SINGLE SOURCE. The web renderer imports this array; it does not keep a copy.
 */
export const WITHHELD_GOVERNED_FIGURE_PHRASES: readonly string[] =
  Object.freeze([
    'a figure that still needs evidence',
    WITHHELD_GOVERNED_FIGURE_CELL_BY_CAUSE.source_pending,
    WITHHELD_GOVERNED_FIGURE_CELL_BY_CAUSE.not_computable,
  ]);

/**
 * The default withheld table cell — the source-pending phrasing, which is the
 * cause the estate's existing emitters carry. Kept as a named export because
 * emitters and suites reference it directly; new emitters should prefer
 * `withheldGovernedFigureCell(cause)` so the cause is stated, not assumed.
 */
export const WITHHELD_GOVERNED_FIGURE_CELL: string =
  WITHHELD_GOVERNED_FIGURE_CELL_BY_CAUSE.source_pending;

/** The cell string a producer must place for a given withholding cause. */
export function withheldGovernedFigureCell(
  cause: WithheldGovernedFigureCause,
): string {
  return (
    WITHHELD_GOVERNED_FIGURE_CELL_BY_CAUSE[cause] ??
    WITHHELD_GOVERNED_FIGURE_CELL
  );
}

/**
 * One metric the producer computed AS WITHHELD, ready to be emitted as a named
 * row. A contract, not a per-module `facts` convention: any module with a
 * compute-side withhold declares the same four things.
 *
 * `withheldReason` is the PRODUCER's own sentence about why no figure exists.
 * It is reader-facing prose, never an identifier and never a code path.
 *
 * `cause` is the TYPED counterpart of that sentence, so the cell beside the
 * reason cannot contradict it.
 */
export type WithheldGovernedMetricRow = Readonly<{
  /** The governed metric key the withheld figure would have carried. */
  metricKey: string;
  /** The metric's governed label — the same label a bound row would show. */
  label: string;
  /** The producer's reader-facing reason no governed figure exists. */
  withheldReason: string;
  /** Why the figure is absent, typed. Drives which closed phrase the cell gets. */
  cause: WithheldGovernedFigureCause;
}>;

function normalizeWithheldCell(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return value.trim().toLowerCase().replace(/[.\s]+$/, '');
}

/** True when a cell value is ENTIRELY a producer withheld-figure phrase. */
export function isWithheldGovernedFigureCell(value: unknown): boolean {
  const normalized = normalizeWithheldCell(value);
  return normalized !== null && WITHHELD_GOVERNED_FIGURE_PHRASES.includes(normalized);
}

/**
 * The cause a withheld cell declares, or `null` when the value is not one.
 *
 * The renderer reads this to choose reason-typed gap copy: telling a reader to
 * connect a source for a ratio whose denominator is not positive is a
 * fabricated cause, and this is how the render side avoids it without
 * classifying prose.
 */
export function withheldGovernedFigureCause(
  value: unknown,
): WithheldGovernedFigureCause | null {
  const normalized = normalizeWithheldCell(value);
  if (normalized === null) return null;
  for (const cause of WITHHELD_GOVERNED_FIGURE_CAUSES) {
    if (WITHHELD_GOVERNED_FIGURE_CELL_BY_CAUSE[cause] === normalized) {
      return cause;
    }
  }
  // The legacy prose-omission phrase predates the cause split and asserts an
  // evidence gap, which is `source_pending`. Recognised, never re-labelled.
  return isWithheldGovernedFigureCell(value) ? 'source_pending' : null;
}
